import 'react-responsive-carousel/lib/styles/carousel.min.css';
import async from 'async';
import * as cookie from 'cookie';
import { useTheme } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { http } from '../../../lib/api/http';
import { V2, V3, V4 } from '../../../lib/api/urls';
import {
  activeStatus,
  archiveStatus,
  closeStatus,
  displayInBlock,
  inactiveStatus,
} from '../../../constants/events';
import { Layout } from '../../../components/layout/Layout';
import { Container } from '../../../components/layout/Container';
import {
  CITIES_API_LIMIT,
  DEVICE_SCREENS_MAX_STRING,
} from '../../../lib/consts';
import { BreadCrumbs } from '../../../components/blocks/BreadCrumbs';
import { AsideEventBlock } from '../../../components/blocks/AsideEventBlock';
import { EventBanner } from '../../../components/blocks/EventBanner';
import { ShareBlock } from '../../../components/blocks/ShareBlock';
import { Link } from '../../../components/styled/Link';
import { Image } from '../../../components/picture/picture';
import CheckMarkSVG from '../../../public/img/check-mark.svg';
import ServiceFreeSVG from '../../../public/img/advantages/service_free.svg';
import LockSVG from '../../../public/img/advantages/lock.svg';
import BankCardSVG from '../../../public/img/advantages/bank_card.svg';
import CancelSVG from '../../../public/img/advantages/cancel.svg';
import CircleSVG from '../../../public/img/advantages/circle.svg';
import paymentOnSpotImg from '../../../public/img/advantages/payment-on-spot.png';
import PushkinSVG from '../../../public/img/pushkin.svg';
import { Description } from '../../../components/blocks/Description';
import { LabelEvent } from '../../../components/styled/LabelEvent';
import { AskQuestion } from '../../../components/collapsable/collapsable';
import { CommentsBlock } from '../../../components/comments/CommentsBlock';
import { Subscription } from '../../../components/about/Subscription';
import { MobileApps } from '../../../components/about/MobileApps';
import { Viewed } from '../../../components/viewed/viewed';
import { fetchViewed } from '../../../utils/viewed';
import { UpButton } from '../../../components/common/UpButton';
import { useModal } from '../../../providers/modal';
import { ActionWidget } from '../../../components/blocks/ActionWidget';
import { PictureModal } from '../../../components/picture/PictureModal';
import {
  BuyButton,
  GBButton,
  RedButton,
} from '../../../components/styled/Button';
import {
  getBuyButtonText,
  getCancellationPolicyText,
  hasOwnPolyfill,
  isEventPassed,
  useSendMetricsFromButton,
} from '../../../utils/event';
import { BookingForm } from '../../../components/booking-form';
import { Action, ACTION_TYPES, PaymentItem } from '../../../models/action';
import { City, Social } from '../../../models';
import { PaymentModal } from '../../../components/booking-form/payment-modal';
import SubscribeForm from '../../../components/form/SubscribeForm';
import SubscribeModal from '../../../components/subscribe/subscribe-modal';
import SubscribeModalForm from '../../../components/form/SubscribeForm/modal';
import FireLogoSVG from '../../../public/img/fire-logo.svg';
import wordDeclension from '../../../utils/word-declension';
import { useAuthorization } from '../../../providers/authorization';
import { Category } from '../../../models/categories';
import { initialModalState } from '../../../models/modal';
import { ImagesSlider } from '../../../components/actions/images-slider';
import { GB_DOMAIN } from '../../../lib/api/apiUrl';
import { setCacheControlHeader } from '../../../utils';
import {
  ModalContent,
  ModalContentWrapper,
  ModalTitleWrapper,
} from '../../../components/common/Modal';
import { CloseSVG } from '../../../components/styled/Close';
import { SimilarEventsBlock } from '../../../components/actions/SimilarEventsBlock';
import { PersonsBlock } from '../../../components/actions/PersonalsBlock';
import { ConfigTypes } from '../../../models/config';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../../../lib/api/browserStorage';
import { useHistory } from '../../../providers/history';

function PageComponent({
  currentCity,
  eventData,
  promos,
  socials,
  gbReccomendLink,
  configOfSite,
}: {
  currentCity: City;
  eventData: Action;
  promos: { results: any };
  socials: { results: Social[] };
  gbReccomendLink: string | null;
  configOfSite: ConfigTypes;
}) {
  const auth = useAuthorization();
  const theme = useTheme();
  const [viewed, setViewed] = useState<{ results: Action[] }>({ results: [] });
  const [isShowBuyButton, setIsShowBuyButton] = useState(false);
  const [tsNextWidgetConfig, setTsNextWidgetConfig] = useState<null | {
    jsUrl: string;
    appId: string;
    id: string;
    type: string;
  }>(null);
  const modalState = useModal();
  const [imageModal, setImageModal] = useState<{
    slide: number | null;
    visible: boolean;
  }>({ slide: null, visible: false });
  const router = useRouter();
  const seoAnalyticsFromButton = useSendMetricsFromButton();
  const history = useHistory();

  const buyButtonRef = useRef<HTMLDivElement>(null);

  const isArchive = () => {
    if (isEventPassed(eventData) && !eventData.date_unknown) {
      return true;
    }
    return (
      eventData.status === archiveStatus ||
      eventData.status === closeStatus ||
      eventData.status === inactiveStatus
    );
  };

  useEffect(() => {
    async function fetch() {
      const viewedList = await fetchViewed(eventData.id);
      setViewed(viewedList);
    }
    fetch();
    let viewedActions = [];
    viewedActions =
      (JSON.parse(getLocalStorageItem('viewed-events') || '[]') || [])
        .filter((id: number) => id !== eventData.id)
        .slice(0, 8) || [];
    setLocalStorageItem(
      'viewed-events',
      JSON.stringify([eventData.id, ...viewedActions])
    );
  }, [eventData.id]);

  useEffect(() => {
    const onScroll = () => {
      const element = buyButtonRef.current;
      const isScrolled = !!element && element.getBoundingClientRect().top < 0;
      setIsShowBuyButton(isScrolled);
    };
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    http.post(V2.eventViews, {
      Action: eventData.remote_id,
    });

    if (eventData.type === 'tsnext_widget') {
      const tsNextWidgetJsUrl = configOfSite.find(
        (item) => item.name === 'TSNEXT_JS_URL'
      );
      const appIdNextWidget = configOfSite.find(
        (item) => item.name === 'TSNEXT_APP_TOKEN'
      );

      setTsNextWidgetConfig({
        jsUrl: `${tsNextWidgetJsUrl?.value}`,
        appId: `${appIdNextWidget?.value}`,
        id: eventData.tsnext_id,
        type: eventData.tsnext_type,
      });
    } else {
      setTsNextWidgetConfig(null);
    }
  }, [eventData.remote_id]);

  const isRanOutOfTickets =
    eventData.event_categories
      .filter((cat) => cat.is_active)
      .filter((cat) => cat.is_unlimited || cat.quantity).length === 0;

  useEffect(() => {
    if (
      router.isReady &&
      hasOwnPolyfill(router.query, 'order') &&
      eventData.not_season
    ) {
      return () => modalState?.setModalState(initialModalState);
    }
    if (
      router.isReady &&
      hasOwnPolyfill(router.query, 'order') &&
      eventData.type !== ACTION_TYPES.tsnext_widget
    ) {
      if (eventData.type === ACTION_TYPES.partner_widget) {
        return modalState?.setModalState((old) => ({
          ...old,
          show: true,
          content: (
            <ModalContentWrapper>
              <ModalTitleWrapper
                showClose
                setModalState={modalState?.setModalState}
                customClose={
                  <CloseSVG
                    onClick={() => {
                      modalState?.setModalState(initialModalState);
                      const url = new URL(window.location.href);
                      url.searchParams.delete('order');
                      router.replace(url.href, undefined, { shallow: true });
                    }}
                    css={{ width: 20, height: 20 }}
                  />
                }
              />
              <ModalContent>
                <ActionWidget action={eventData} />
              </ModalContent>
            </ModalContentWrapper>
          ),
          options: {},
          outsideCloseCallback: () => {
            const url = new URL(window.location.href);
            url.searchParams.delete('order');
            router.replace(url.href, undefined, { shallow: true });
          },
        }));
      }
      modalState?.setModalState((old) => ({
        ...old,
        show: true,
        content: (
          <BookingForm
            setModalState={modalState.setModalState}
            actionData={eventData}
            gbReccomendLink={gbReccomendLink}
            currentCity={currentCity}
            banOnSomeEmails={
              !!configOfSite.find(
                (item) => item.name === 'WARN_ON_RESTRICTED_EMAIL_DOMAINS'
              )?.value
            }
            banEmails={configOfSite.find(
              (item) => item.name === 'RESTRICTED_EMAIL_DOMAINS'
            )}
          />
        ),
        options:
          eventData.type === ACTION_TYPES.purchase ? { width: '50%' } : {},
        outsideCloseCallback: () => {
          const url = new URL(window.location.href);
          url.searchParams.delete('order');
          router.replace(url.href, undefined, { shallow: true });
        },
      }));
    }
    return () => modalState?.setModalState(initialModalState);
  }, [router.query.order]);

  useEffect(() => {
    const { payment_status: paymentStatus } = router.query;

    if (paymentStatus) {
      const reservationId = getLocalStorageItem(ACTION_TYPES.reservation);
      if (reservationId) {
        http
          .get(V4.reservationsItem(reservationId), null, null, {
            contentType: 'json',
            withTokenRequest: true,
          })
          .then((reservationResponse) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (window.dataLayer.find((item) => item.ecommerce)) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              window.dataLayer.push({ ecommerce: null });
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.dataLayer.push({
              ecommerce: {
                currencyCode: 'RUB',
                purchase: {
                  actionField: {
                    id: reservationResponse.id,
                  },
                  products: reservationResponse.payment_items.map(
                    (item: PaymentItem) => ({
                      id: reservationResponse.event.id,
                      name: reservationResponse.event.title,
                      price: item.price,
                      category: reservationResponse.event.marketing_category,
                      variant: item.name,
                      quantity: item.quantity,
                    })
                  ),
                },
              },
            });
          });
      }

      modalState?.setModalState((old) => ({
        ...old,
        show: true,
        content: (
          <PaymentModal
            gbReccomendLink={gbReccomendLink}
            status={paymentStatus}
            setModalState={modalState.setModalState}
          />
        ),
        outsideCloseCallback: () => {
          modalState.setModalState(initialModalState);
          if (gbReccomendLink && paymentStatus === 'success') {
            router.push(gbReccomendLink);
          }
        },
      }));
    }
  }, [router.query.payment_status, router.query.orderId]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (window.dataLayer) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (window.dataLayer.find((item) => item.ecommerce)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.dataLayer.push({ ecommerce: null });
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.dataLayer.push({
        ecommerce: {
          currencyCode: 'RUB',
          detail: {
            products: [
              {
                id: eventData.id,
                name: eventData.title,
                price: eventData.filter_price,
                category: eventData.marketing_category,
              },
            ],
          },
        },
      });
    }
  }, [eventData]);

  function onSuccessSubscribe(email: string) {
    modalState?.setModalState((old) => ({
      ...old,
      show: true,
      content: (
        <SubscribeModal notSeason={eventData.not_season} email={email} />
      ),
    }));
  }
  function onClickBuyButton() {
    seoAnalyticsFromButton.onSend(eventData, isRanOutOfTickets);
    const url = new URL(window.location.href);
    if (isRanOutOfTickets || eventData.not_season) {
      if (eventData.subscription_confirmed) {
        return;
      }
      const email = auth?.getUser()?.email;
      if (email) {
        http
          .post(V2.subscriptions, {
            source: eventData.not_season
              ? 'SeasonStart'
              : 'ArchiveSubscription',
            event_id: eventData.id,
            city: currentCity.remote_id,
            email: auth?.getUser()?.email,
          })
          .then(() => {
            onSuccessSubscribe(email);
          });
      } else {
        modalState?.setModalState((old) => ({
          ...old,
          show: true,
          content: (
            <SubscribeModalForm
              notSeason={eventData.not_season}
              eventId={eventData.id}
              currentCity={currentCity}
              onSuccessSubscribe={onSuccessSubscribe}
            />
          ),
        }));
      }
      return;
    }
    switch (eventData.type) {
      case 'certificate':
      case 'reservation':
      case 'purchase':
      case 'FREE':
      case 'partner_widget':
        url.searchParams.append('order', '');
        router.replace(url.href, undefined, { shallow: true });
        break;
      default:
        break;
    }
  }

  const createBreadCrumbs = () => {
    if (
      history &&
      history.lastPage &&
      history.lastPage.type === 'compilation'
    ) {
      return [
        {
          title: 'Подборки',
          url: 'compilations',
        },
        {
          title: history.lastPage.title || '',
          href: history.lastPage.url,
        },
        { title: eventData.title },
      ];
    }
    return [
      {
        title: 'Каталог',
        url: 'catalog',
      },
      { title: eventData.title },
    ];
  };

  return (
    <Container
      css={{
        padding: '15px 12px',
        [DEVICE_SCREENS_MAX_STRING.sm]: {
          padding: '0 !important',
        },
      }}
    >
      {tsNextWidgetConfig && (
        <>
          <Script src={tsNextWidgetConfig.jsUrl} />
          <Script id='for-new-widget'>{`var ppid = "${tsNextWidgetConfig.appId}"`}</Script>
        </>
      )}
      <div
        css={{
          marginBottom: 16,
          [DEVICE_SCREENS_MAX_STRING.lg]: {
            display: 'none',
          },
        }}
      >
        <BreadCrumbs currentCity={currentCity} items={createBreadCrumbs()} />
      </div>
      <main css={{ fontFamily: 'Golos', color: theme.colors.black }}>
        <EventBanner
          disabled={isArchive()}
          item={eventData}
          onButtonClick={() => onClickBuyButton()}
          isRanOutOfTickets={isRanOutOfTickets}
          tsNextWidgetConfig={tsNextWidgetConfig}
        />
        {!eventData.date_unknown && (
          <div
            ref={buyButtonRef}
            css={{
              display: 'none',
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                display: 'block',
                width: `100%`,
                paddingRight: 20,
                paddingLeft: 20,
                position: `absolute`,
                zIndex: 2,
              },
            }}
          >
            <RedButton
              callback={() => onClickBuyButton()}
              disabled={isArchive()}
              full
              montserrat
              subscription_confirmed={
                eventData.status === activeStatus &&
                eventData.subscription_confirmed
              }
              {...(tsNextWidgetConfig && {
                'data-type': tsNextWidgetConfig.type,
                'data-uid': tsNextWidgetConfig.id,
                className: 'tsnext-widget',
              })}
            >
              {eventData.status === activeStatus &&
                eventData.subscription_confirmed && (
                  <CheckMarkSVG css={{ width: 16, height: 14 }} />
                )}
              {getBuyButtonText(eventData, isRanOutOfTickets)}
            </RedButton>
          </div>
        )}
        <div
          itemScope
          itemType='https://schema.org/Event'
          css={{
            display: 'flex',
            marginTop: 30,
            marginBottom: 50,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              flexDirection: 'column',
              padding: '0 8px',
              marginTop: 0,
            },
          }}
        >
          <span css={{ display: 'none' }} itemProp='name'>
            {eventData.title}
          </span>
          <div
            css={{
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                marginTop: 50,
              },
            }}
            className='col-12 col-md-5 col-lg-4 col-xl-3'
          >
            {eventData.type !== 'FREE' &&
              eventData.reservation_count_text &&
              !eventData.countdown_tickets && (
                <p
                  css={{
                    marginBottom: 16,
                    fontSize: 14,
                    color: '#282828',
                    fontFamily: "'Montserrat', serif",
                  }}
                >
                  {eventData.reservation_count_text}
                </p>
              )}
            {eventData.type !== 'FREE' && eventData.countdown_tickets ? (
              <p
                css={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  marginTop: 10,
                  marginBottom: 18,
                  color: '#282828',
                }}
              >
                <FireLogoSVG css={{ width: 18, height: 18 }} />
                <span
                  css={{
                    fontSize: 14,
                    fontFamily: 'Montserrat, serif',
                  }}
                >
                  {wordDeclension(
                    eventData.countdown_tickets,
                    'Остался',
                    'Осталось',
                    'Осталось'
                  )}{' '}
                  <b css={{ fontWeight: 'bolder' }}>
                    {eventData.countdown_tickets}
                  </b>{' '}
                  {wordDeclension(
                    eventData.countdown_tickets,
                    'билет',
                    'билета',
                    'билетов'
                  )}
                </span>
              </p>
            ) : (
              ''
            )}
            <div
              css={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexWrap: 'wrap',
                gap: 6,
                marginBottom: 24,
              }}
            >
              {eventData.not_season && (
                <LabelEvent text='Не сезон' description='Не сезон' />
              )}
              {eventData.hit && (
                <LabelEvent
                  text='хит'
                  description='Пользователи часто просматривают это мероприятие'
                />
              )}
              {eventData.premiere && (
                <LabelEvent
                  text='премьера'
                  description='Премьерные показы мероприятия'
                />
              )}
              {eventData.first_time && (
                <LabelEvent
                  text='впервые'
                  description='Событие размещено на сайте впервые'
                />
              )}
              {eventData.best_reviews && (
                <LabelEvent
                  text='лучшие отзывы'
                  description='Пользователи высоко оценивают мероприятие'
                />
              )}
              {eventData.prepayment && (
                <LabelEvent
                  text='предоплата'
                  description='Онлайн вы вносите только предоплату'
                />
              )}
            </div>
            <AsideEventBlock item={eventData} currentCity={currentCity} />
            {eventData.pushkin_card_payment && (
              <div
                css={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1.5px solid #F44336',
                  padding: 16,
                  gap: 16,
                }}
              >
                <PushkinSVG
                  css={{
                    width: 44,
                    height: 44,
                  }}
                />
                <span
                  css={{
                    width: 220,
                    fontSize: 14,
                    fontWeight: 400,
                    color: theme.colors.text,
                  }}
                >
                  событие можно оплатить Пушкинской картой
                </span>
              </div>
            )}
            <div
              css={{
                marginTop: 20,
                textAlign: 'center',
                [DEVICE_SCREENS_MAX_STRING.sm]: {
                  display: 'none',
                },
              }}
            >
              {!isEventPassed(eventData) &&
                eventData.dates_txt &&
                !isRanOutOfTickets && (
                  <GBButton
                    callback={() => onClickBuyButton()}
                    text={getBuyButtonText(eventData, isRanOutOfTickets)}
                    size='sm'
                    modification='shadow'
                    buttonStyle={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '19px',
                      // width: '220px',
                      fontFamily: 'Golos',
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '16px',
                      ...(isArchive() ? { background: '#A5A2A1' } : {}),
                    }}
                    textStyle={{
                      textAlign: 'center',
                    }}
                    {...(tsNextWidgetConfig && {
                      'data-type': tsNextWidgetConfig.type,
                      'data-uid': tsNextWidgetConfig.id,
                      className: 'tsnext-widget',
                    })}
                  />
                )}
            </div>
            {promos.results && promos.results.length > 0 && (
              <div css={{ marginTop: 20 }}>
                <Link href={[promos.results[0].url].join('/')}>
                  <Image image={promos.results[0].image} alt='промо' />
                </Link>
              </div>
            )}
            {eventData.status === activeStatus &&
              isRanOutOfTickets &&
              (eventData.subscription_confirmed ? (
                <div
                  css={{
                    ...theme.mixins.fonts.golos,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 15,
                    marginBottom: 10,
                    padding: '24px 16px',
                    backgroundColor: theme.colors.white,
                    color: theme.colors.text,
                    height: 'auto',
                    alignItems: 'flex-start',
                    svg: {
                      paddingTop: 5,
                    },
                  }}
                >
                  <CheckMarkSVG css={{ width: 24, minWidth: 24 }} />
                  <span>
                    Мы сообщим вам, когда билеты на событие поступят в продажу
                  </span>
                </div>
              ) : (
                <div
                  css={{
                    ...theme.mixins.fonts.golos,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 15,
                    height: 180,
                    marginBottom: 10,
                    color: theme.colors.white,
                    padding: '24px 16px',
                    backgroundColor: theme.colors.red,
                  }}
                >
                  <p>
                    Оставьте e-mail, чтобы мы сообщили, когда билеты снова
                    появятся в продаже
                  </p>
                  <SubscribeForm
                    source='ArchiveSubscription'
                    eventId={eventData.id}
                    currentCity={currentCity}
                    onSuccessSubscribe={onSuccessSubscribe}
                  />
                </div>
              ))}
            <div
              css={{
                margin: '25px 0',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                fontFamily: 'Golos',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '16px',
                color: '#7D7B7A',
                textAlign: 'left',
              }}
            >
              {eventData.service_fee === 0 && (
                <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <ServiceFreeSVG css={{ width: 24 }} />
                  <p>без сервисного сбора</p>
                </div>
              )}
              {eventData.service_fee && eventData.service_fee > 0 ? (
                <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div css={{ position: 'relative' }}>
                    <CircleSVG css={{ width: 24 }} />
                    <div
                      css={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        fontFamily: 'Montserrat',
                        fontSize: 10,
                        fontWeight: 500,
                        color: theme.colors.red,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <p>{eventData.service_fee}%</p>
                    </div>
                  </div>
                  <p>сервисный сбор</p>
                </div>
              ) : (
                ''
              )}
              {['purchase', 'certificate', 'tsnext_widget'].includes(
                eventData.type
              ) && (
                <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <LockSVG css={{ width: 24, minWidth: 24 }} />
                  <p>защита персональных данных</p>
                </div>
              )}
              {['purchase', 'certificate', 'tsnext_widget'].includes(
                eventData.type
              ) && (
                <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <BankCardSVG css={{ width: 24, minWidth: 24 }} />
                  <p>безопасные платежи картой любого банка</p>
                </div>
              )}
              {eventData?.cancellation_policy && (
                <div
                  css={{
                    display: 'flex',
                    gap: 10,
                    alignItems:
                      eventData.cancellation_policy === '10_days'
                        ? 'flex-start'
                        : 'center',
                  }}
                >
                  <CancelSVG css={{ width: 24, minWidth: 24 }} />
                  <p>
                    {getCancellationPolicyText(eventData.cancellation_policy)}
                  </p>
                </div>
              )}
              {eventData.type === 'reservation' &&
                eventData.payment_on_spot && (
                  <div css={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <img
                      src={paymentOnSpotImg.src}
                      alt='бесплатное бронирование'
                      css={{ width: 24, minWidth: 24 }}
                    />
                    <p>бесплатное бронирование</p>
                  </div>
                )}
            </div>
          </div>
          <div
            className='col-12 col-md-6 push-md-1 col-lg-7 push-lg-1'
            css={{
              position: 'relative',
              left: '8.33333333%',
              [DEVICE_SCREENS_MAX_STRING.md]: {
                left: 0,
              },
            }}
          >
            <div css={{ marginBottom: 15 }}>
              <ShareBlock item={eventData} />
            </div>
            <div
              css={{
                marginBottom: 40,
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  '.crop-text': { margin: '0 8px 40px 8px' },
                },
              }}
              id='actions__description-block'
            >
              <Description
                title='Описание'
                description={eventData.description}
              />
              <div
                css={{ display: 'none' }}
                itemProp='description'
                dangerouslySetInnerHTML={{
                  __html: `${eventData.description.slice(0, 999)}...`,
                }}
              />
            </div>
            {eventData.block_1_title && (
              <div css={{ marginBottom: 40 }}>
                <Description
                  title={eventData.block_1_title}
                  description={
                    eventData.block_1_description_html ??
                    eventData.block_1_description
                  }
                />
              </div>
            )}
            {eventData.block_2_title && (
              <div css={{ marginBottom: 40 }}>
                <Description
                  title={eventData.block_2_title}
                  description={
                    eventData.block_2_description_html ??
                    eventData.block_2_description
                  }
                />
              </div>
            )}
            {(eventData.images.length > 1 || eventData.video_url) && (
              <ImagesSlider
                videoInfo={{
                  video: eventData.video_url,
                  cover: eventData.video_cover,
                }}
                images={eventData.images}
                onSlideClick={(index: number) =>
                  setImageModal({ slide: index, visible: true })
                }
              />
            )}
            {eventData.condition_title && (
              <div css={{ marginBottom: 40 }}>
                <Description
                  title={eventData.condition_title}
                  description={eventData.condition}
                />
              </div>
            )}
            {!!socials?.results.length && (
              <div css={{ marginBottom: 36 }}>
                <AskQuestion socials={socials} />
              </div>
            )}
            <PersonsBlock eventData={eventData} />
            {isShowBuyButton && !eventData.date_unknown && (
              <div
                css={{
                  display: 'none',
                  [DEVICE_SCREENS_MAX_STRING.sm]: {
                    display: 'block',
                  },
                  marginBottom: 0,
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: theme.colors.white,
                  zIndex: 100,
                  padding: 0,
                  boxShadow: `0 0 5px rgba(0, 0, 0, 0.2)`,
                }}
              >
                <BuyButton
                  callback={() => onClickBuyButton()}
                  disabled={isArchive()}
                  full
                  subscription_confirmed={
                    eventData.status === activeStatus &&
                    eventData.subscription_confirmed
                  }
                  {...(tsNextWidgetConfig && {
                    'data-type': tsNextWidgetConfig.type,
                    'data-uid': tsNextWidgetConfig.id,
                    className: 'tsnext-widget',
                  })}
                >
                  {eventData.status === activeStatus &&
                    eventData.subscription_confirmed && (
                      <CheckMarkSVG css={{ width: 16, height: 14 }} />
                    )}
                  {getBuyButtonText(eventData, isRanOutOfTickets)}
                </BuyButton>
              </div>
            )}
            <div>
              <CommentsBlock
                contentType='events.event'
                eventId={eventData.id}
                currentCity={currentCity}
              />
            </div>
          </div>
        </div>
        <div
          css={{
            '.actions__item': {
              width: 320,
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              '.actions__item': {
                width: 270,
              },
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              padding: '0 8px',
              '.actions__item': {
                maxWidth: '77%',
                width: '100%',
              },
            },
          }}
        >
          <SimilarEventsBlock eventData={eventData} currentCity={currentCity} />
          <Viewed title='Вы смотрели' viewed={viewed} />
        </div>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: '4rem',
            marginTop: '2rem',
            [DEVICE_SCREENS_MAX_STRING.xl]: {
              gridTemplateColumns: '1fr',
              padding: '0 8px',
            },
          }}
        >
          <Subscription currentCity={currentCity} />
          <MobileApps />
        </div>
        <UpButton elementId='actions__description-block' screenCount={1.5} />
        {!!imageModal.visible &&
          (!!eventData.images?.length || !!eventData.video_url) && (
            <PictureModal
              videoInfo={{
                video: eventData.video_url,
                cover: eventData.video_cover,
              }}
              images={eventData.images.slice(1)}
              imageModal={imageModal}
              scrollY={window.scrollY}
              setVisible={setImageModal}
            />
          )}
      </main>
    </Container>
  );
}

export default function Page({
  currentCity,
  cities,
  eventData,
  footerProps,
  promos,
  configOfSite,
  categoriesForHeader,
  gorbiletRekomenduet,
}: any) {
  const gbReccomendLink = gorbiletRekomenduet.slug
    ? ['', currentCity.url, 'compilations', gorbiletRekomenduet.slug, ''].join(
        '/'
      )
    : null;

  return (
    <Layout
      footerProps={footerProps}
      currentCity={currentCity}
      cities={cities}
      categoriesForHeader={categoriesForHeader}
      seoTitle={
        eventData.seo_title && eventData.seo_title !== ''
          ? eventData.seo_title
          : eventData.title
      }
      seoDescription={
        eventData.seo_description && eventData.seo_description !== ''
          ? eventData.seo_description
          : ''
      }
      canonicalLink={`${GB_DOMAIN}/${currentCity.url}/actions/${eventData.slug}/`}
    >
      <PageComponent
        configOfSite={configOfSite}
        currentCity={currentCity}
        eventData={eventData}
        promos={promos}
        gbReccomendLink={gbReccomendLink}
        socials={footerProps.socials}
      />
    </Layout>
  );
}

export async function getServerSideProps(context: any) {
  setCacheControlHeader(context);
  const cities = await http.get(V3.cities, { limit: CITIES_API_LIMIT });
  let currentCityUrl: any;
  let parsedCookies = {};
  if (context.req.headers.cookie) {
    parsedCookies = cookie.parse(context.req.headers.cookie);
  }
  if (context.query.city) {
    currentCityUrl = context.query.city;
  } else if ((parsedCookies as any)['current-city']) {
    currentCityUrl = (parsedCookies as any)['current-city'];
  } else {
    currentCityUrl = cities.results[0].url;
  }

  const currentCity = cities.results.find(
    (city: any) => city.url === currentCityUrl
  );

  if (!currentCity) {
    return { notFound: true };
  }

  // убрать параллельные запросы при переезде на стачискую генерацию
  const data: any = await async.parallel({
    eventData: (callback) =>
      http
        .get(V3.eventsSlug(context.params.slug))
        .then((res) => callback(null, res))
        .catch((err) => callback(null, { error: err })),
    promos: (callback) =>
      http
        .get(V2.promos, {
          limit: 1,
          city: currentCity.id,
          status: activeStatus,
          ordering: 'sort_order',
        })
        .then((res) => callback(null, res))
        .catch((err) => callback(null, { error: err })),
    pageList: (callback) =>
      http
        .get(V2.pages, {
          city: null,
          status: activeStatus,
          show_in_footer: true,
        })
        .then((res) => callback(null, res))
        .catch((err) => callback(null, { error: err })),
    socials: (callback) =>
      http
        .get(V2.socials, {
          display_in_block: displayInBlock.contactUs,
          status: activeStatus,
          city: currentCity.id,
        })
        .then((res) => callback(null, res))
        .catch((err) => callback(null, { error: err })),
    socialsLinks: (callback) =>
      http
        .get(V2.socials, {
          display_in_block: displayInBlock.weInSocials,
          status: activeStatus,
          city: currentCity.id,
        })
        .then((res) => callback(null, res))
        .catch((err) => callback(null, { error: err })),
    contactList: (callback) =>
      http
        .get(V2.contacts, {
          limit: 9e9,
          status: activeStatus,
          city: currentCity.id,
        })
        .then((res) => callback(null, res))
        .catch((err) => callback(null, { error: err })),
    adpCategories: (callback) =>
      http
        .get(V3.adpCategories(currentCity.id))
        .then((res) => callback(null, res))
        .catch((err) => callback(null, { error: err })),
    parentCategories: (callback) =>
      http
        .get(V3.categories, {
          city: currentCity.id,
          is_web: true,
          status: activeStatus,
          sort: 'sortOrder',
          limit: 1000,
          level: 0,
        })
        .then((res) => callback(null, res))
        .catch((err) => callback(null, { error: err })),
    configOfSite: (callback) =>
      http
        .get(V2.config, {})
        .then((res) => callback(null, res))
        .catch((err) => callback(null, { error: err })),
    compilations: (callback) =>
      http
        .get(V3.compilations.root, {
          city: currentCity.id,
          is_gb_recommended: true,
        })
        .then((res) => callback(null, res))
        .catch((err) => callback(null, { error: err })),
  });

  if (data.eventData.error?.statusCode === 404) {
    return { notFound: true };
  }

  const categoriesForHeader = [];

  for (let i = 0; i < data.adpCategories.categories.length; i += 1) {
    const el = data.parentCategories.results.find(
      (item: Category) => item.id === data.adpCategories.categories[i]
    );
    if (el) {
      categoriesForHeader.push(el);
    }
  }

  return {
    props: {
      currentCity,
      cities,
      eventData: (data as any).eventData,
      promos: (data as any).promos,
      categoriesForHeader,
      configOfSite: data.configOfSite,
      gorbiletRekomenduet: {
        slug: data.compilations.results[0]?.slug || null,
      },
      footerProps: {
        pageList: (data as any).pageList,
        socials: (data as any).socials,
        socialsLinks: (data as any).socialsLinks,
        contactList: (data as any).contactList,
      },
    },
  };
}
